import { useEffect, useState } from "react";
import api, { formatErr } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Landmark, ArrowDownLeft, ArrowUpRight, Save, Trash2, RefreshCw, Plus, FileSpreadsheet, FileText } from "lucide-react";

const emptyOut = { tx_type: "out", tx_date: new Date().toISOString().slice(0, 10), bank_id: "", bank_name: "", debit_name: "", credit_name: "", amount: "", payment_type: "", operator: "" };
const emptyIn  = { tx_type: "in",  tx_date: new Date().toISOString().slice(0, 10), bank_id: "", bank_name: "", doc_no: "", receipt_id: "", service_name: "", assigned_person: "", amount: "", operator: "" };
const shortBill = (v) => { if (!v) return "—"; const p = String(v).split("/"); const last = p[p.length - 1] || v; const m = String(last).match(/\d+/); return (m ? m[0] : String(last)).padStart(7, "0"); };

const RANGES = ["Custom", "This Month", "Last Month", "This Quarter", "Last Quarter", "This Year", "Last Year"];
function rangeToDates(rng) {
  const t = new Date(); const iso = (d) => d.toISOString().slice(0, 10);
  const y = t.getFullYear(); const m = t.getMonth();
  const monthStart = (yy, mm) => new Date(yy, mm, 1);
  const monthEnd   = (yy, mm) => new Date(yy, mm + 1, 0);
  switch (rng) {
    case "This Month":   return { from: iso(monthStart(y, m)),      to: iso(t) };
    case "Last Month":   return { from: iso(monthStart(y, m - 1)),  to: iso(monthEnd(y, m - 1)) };
    case "This Quarter": { const qs = Math.floor(m / 3) * 3; return { from: iso(monthStart(y, qs)), to: iso(t) }; }
    case "Last Quarter": { const qs = Math.floor(m / 3) * 3 - 3; const ys = qs < 0 ? y - 1 : y; const qm = ((qs % 12) + 12) % 12; return { from: iso(monthStart(ys, qm)), to: iso(monthEnd(ys, qm + 2)) }; }
    case "This Year":    return { from: iso(new Date(y, 0, 1)),     to: iso(t) };
    case "Last Year":    return { from: iso(new Date(y - 1, 0, 1)), to: iso(new Date(y - 1, 11, 31)) };
    default:             return null;
  }
}

export default function BankTransactions() {
  const { user: currentUser } = useAuth();
  const [tab, setTab] = useState("in");
  const [banks, setBanks] = useState([]);
  const [users, setUsers] = useState([]);
  const [paymentTypes, setPaymentTypes] = useState([]);
  const [txList, setTxList] = useState([]);
  const [out, setOut] = useState(emptyOut);
  const [inn, setInn] = useState(emptyIn);
  const [loading, setLoading] = useState(false);
  // Report/filter state
  const today = new Date().toISOString().slice(0, 10);
  const past = new Date(); past.setDate(past.getDate() - 29);
  const [rng, setRng] = useState("This Month");
  const [dateFrom, setDateFrom] = useState(past.toISOString().slice(0, 10));
  const [dateTo, setDateTo] = useState(today);
  // Add-bank inline dialog
  const [showAddBank, setShowAddBank] = useState(false);
  const [newBank, setNewBank] = useState({ name: "", account_number: "", ifsc: "", branch: "" });

  useEffect(() => {
    const d = rangeToDates(rng);
    if (d) { setDateFrom(d.from); setDateTo(d.to); }
  }, [rng]);

  const loadTx = async (t = tab) => {
    setLoading(true);
    try {
      const { data } = await api.get("/bank-transactions", { params: { tx_type: t, date_from: dateFrom, date_to: dateTo } });
      setTxList(data || []);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    api.get("/banks").then((r) => setBanks(r.data || [])).catch(() => {});
    api.get("/users").then((r) => setUsers(r.data || [])).catch(() => {});
    api.get("/payment-types").then((r) => setPaymentTypes(r.data || [])).catch(() => {});
    loadTx();
    // eslint-disable-next-line
  }, []);
  useEffect(() => { loadTx(tab); /* eslint-disable-next-line */ }, [tab, dateFrom, dateTo]);

  const saveNewBank = async () => {
    if (!newBank.name.trim()) { toast.error("Bank name is required"); return; }
    try {
      const { data } = await api.post("/banks", newBank);
      toast.success(`Bank "${data.name}" added`);
      setBanks((b) => [...b, data]);
      setNewBank({ name: "", account_number: "", ifsc: "", branch: "" });
      setShowAddBank(false);
    } catch (e) { toast.error(formatErr(e?.response?.data?.detail)); }
  };

  const downloadReport = async (format) => {
    const path = format === "pdf" ? "/bank-transactions/export-pdf" : "/bank-transactions/export";
    const res = await api.get(path, { params: { tx_type: tab, date_from: dateFrom, date_to: dateTo }, responseType: "blob" });
    const ext = format === "pdf" ? "pdf" : "xlsx";
    const mime = format === "pdf" ? "application/pdf" : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
    const url = window.URL.createObjectURL(new Blob([res.data], { type: mime }));
    const a = document.createElement("a"); a.href = url;
    a.download = `bank-${tab}-${dateFrom}_to_${dateTo}.${ext}`;
    document.body.appendChild(a); a.click(); a.remove();
    window.URL.revokeObjectURL(url);
  };

  const setBank = (setState, form) => (bankId) => {
    const b = banks.find((x) => x.id === bankId);
    setState({ ...form, bank_id: bankId, bank_name: b?.name || "" });
  };

  const lookupDocNo = async (docNo) => {
    if (!docNo) return;
    try {
      const { data } = await api.get("/receipts/lookup/by-doc-no", { params: { doc_no: docNo } });
      setInn((s) => ({ ...s, doc_no: docNo, receipt_id: data.id, service_name: data.service_name || "", assigned_person: data.assigned_person || "", amount: s.amount || data.balance || data.amount || "" }));
      toast.success(`Loaded: ${data.customer_name}`);
    } catch { toast.error("Bill/Receipt not found"); }
  };

  const saveOut = async () => {
    if (!out.bank_id || !out.credit_name || !out.amount) {
      toast.error("Bank, Credit Name and Amount are required"); return;
    }
    try {
      await api.post("/bank-transactions", { ...out, amount: Number(out.amount) });
      toast.success("Payment recorded"); setOut(emptyOut); loadTx("out");
    } catch (e) { toast.error(formatErr(e?.response?.data?.detail)); }
  };
  const saveIn = async () => {
    if (!inn.bank_id || !inn.doc_no || !inn.amount) {
      toast.error("Bank, Bill No and Amount are required"); return;
    }
    try {
      await api.post("/bank-transactions", { ...inn, amount: Number(inn.amount) });
      toast.success("Bill collection recorded"); setInn(emptyIn); loadTx("in");
    } catch (e) { toast.error(formatErr(e?.response?.data?.detail)); }
  };
  const removeTx = async (id) => {
    if (!window.confirm("Delete this transaction?")) return;
    try { await api.delete(`/bank-transactions/${id}`); toast.success("Deleted"); loadTx(); }
    catch (e) { toast.error(formatErr(e?.response?.data?.detail)); }
  };

  const inr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });

  return (
    <div className="space-y-4" data-testid="bank-transactions-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display font-black text-3xl tracking-tight text-slate-900">Bank Transactions</h1>
          <p className="text-xs text-slate-500 mt-0.5">Record payments to service providers and bill collections into your bank accounts.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={() => downloadReport("xlsx")} className="h-8 bg-[color:var(--brand-teal)] hover:bg-[color:var(--brand-teal-hover)] text-white text-xs font-semibold" data-testid="btx-excel">
            <FileSpreadsheet className="h-3.5 w-3.5 mr-1.5" /> Excel
          </Button>
          <Button onClick={() => downloadReport("pdf")} className="h-8 bg-slate-700 hover:bg-slate-800 text-white text-xs font-semibold" data-testid="btx-pdf">
            <FileText className="h-3.5 w-3.5 mr-1.5" /> PDF
          </Button>
          {currentUser?.role === "admin" && (
            <Button
              onClick={async () => {
                try {
                  const res = await api.get(`/bank-transactions/export?all=true${tab !== "all" ? `&tx_type=${tab}` : ""}`, { responseType: "blob" });
                  const url = window.URL.createObjectURL(new Blob([res.data]));
                  const a = document.createElement("a");
                  a.href = url; a.download = `bank-transactions-ALL.xlsx`;
                  document.body.appendChild(a); a.click(); a.remove();
                  window.URL.revokeObjectURL(url);
                  toast.success("Downloaded ALL bank transactions");
                } catch (e) { toast.error(formatErr(e?.response?.data?.detail) || "Failed"); }
              }}
              className="h-8 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold"
              data-testid="btx-excel-all"
              title="Admin: download every historical bank transaction in one click"
            >
              <FileSpreadsheet className="h-3.5 w-3.5 mr-1.5" /> Export ALL
            </Button>
          )}
          <Button onClick={() => loadTx()} disabled={loading} className="h-8 bg-[#2E77E6] hover:bg-[#2361C4] text-white text-xs font-semibold" data-testid="btx-refresh">
            <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </div>
      </div>

      {/* Date/Range filter bar */}
      <div className="bg-white border border-slate-200 rounded-md p-4 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2 text-xs">
          <span className="uppercase tracking-widest font-bold text-slate-500">Range</span>
          <Select value={rng} onValueChange={setRng}>
            <SelectTrigger className="h-8 w-40 text-xs" data-testid="btx-rng"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">{RANGES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <span className="uppercase tracking-widest font-bold text-slate-500">From</span>
          <Input type="date" value={dateFrom} onChange={(e) => { setRng("Custom"); setDateFrom(e.target.value); }} className="h-8 w-36 text-xs" data-testid="btx-from" />
          <span className="uppercase tracking-widest font-bold text-slate-500">To</span>
          <Input type="date" value={dateTo} onChange={(e) => { setRng("Custom"); setDateTo(e.target.value); }} className="h-8 w-36 text-xs" data-testid="btx-to" />
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-2">
        {[
          ["in",  "Bank Collection (Bill Received)", ArrowDownLeft, "#059669"],
          ["out", "Payment for Service Fees", ArrowUpRight, "#DC2626"],
        ].map(([key, label, Icon, color]) => (
          <button key={key} onClick={() => setTab(key)} data-testid={`btx-tab-${key}`}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-semibold border transition ${
              tab === key ? "bg-[color:var(--brand-navy)] text-white border-[color:var(--brand-navy)]" : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
            }`}>
            <Icon className="h-4 w-4" style={{ color: tab === key ? "#F5D77A" : color }} />
            {label}
          </button>
        ))}
      </div>

      {/* Form */}
      <div className="bg-white border border-slate-200 rounded-md p-5">
        <div className="flex items-center gap-2 mb-4">
          <Landmark className="h-4 w-4 text-[color:var(--brand-teal)]" />
          <div className="font-display font-bold text-slate-800 text-sm">{tab === "out" ? "New Bank Payment" : "New Bill Collection"}</div>
        </div>

        {tab === "out" ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Field label="Date"><Input type="date" value={out.tx_date} onChange={(e) => setOut({ ...out, tx_date: e.target.value })} className="h-9" data-testid="out-date" /></Field>
            <Field label="Bank Name *">
              <div className="flex gap-1">
                <Select value={out.bank_id} onValueChange={setBank(setOut, out)}>
                  <SelectTrigger className="h-9 flex-1" data-testid="out-bank"><SelectValue placeholder="Select bank" /></SelectTrigger>
                  <SelectContent className="bg-white">{banks.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
                </Select>
                <Button type="button" onClick={() => setShowAddBank(true)} className="h-9 w-9 p-0 bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-300" title="Add new bank" data-testid="out-add-bank"><Plus className="h-4 w-4" /></Button>
              </div>
            </Field>
            <Field label="Bank Debit Name">
              <Select value={out.debit_name} onValueChange={(v) => setOut({ ...out, debit_name: v })}>
                <SelectTrigger className="h-9" data-testid="out-debit-name"><SelectValue placeholder="Select bank" /></SelectTrigger>
                <SelectContent className="bg-white">{banks.map((b) => <SelectItem key={b.id} value={b.name}>{b.name}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Bank Credit Name *">
              <Select value={out.credit_name} onValueChange={(v) => setOut({ ...out, credit_name: v })}>
                <SelectTrigger className="h-9" data-testid="out-credit-name"><SelectValue placeholder="Select bank" /></SelectTrigger>
                <SelectContent className="bg-white">{banks.map((b) => <SelectItem key={b.id} value={b.name}>{b.name}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Amount *"><Input type="number" value={out.amount} onChange={(e) => setOut({ ...out, amount: e.target.value })} placeholder="0.00" className="h-9 font-mono-num" data-testid="out-amount" /></Field>
            <Field label="Payment Type">
              <Select value={out.payment_type} onValueChange={(v) => setOut({ ...out, payment_type: v })}>
                <SelectTrigger className="h-9" data-testid="out-payment-type"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent className="bg-white">{paymentTypes.filter((p) => p.active !== false).map((p) => <SelectItem key={p.id} value={p.payment_type}>{p.payment_type}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Operator">
              <Select value={out.operator} onValueChange={(v) => setOut({ ...out, operator: v })}>
                <SelectTrigger className="h-9" data-testid="out-operator"><SelectValue placeholder="Select operator" /></SelectTrigger>
                <SelectContent className="bg-white">{users.map((u) => <SelectItem key={u.id} value={u.name}>{u.name}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <div className="md:col-span-3 flex justify-end pt-2">
              <Button onClick={saveOut} className="h-9 bg-red-600 hover:bg-red-700 text-white font-semibold" data-testid="out-save"><Save className="h-4 w-4 mr-1.5" /> Save Payment</Button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Field label="Date"><Input type="date" value={inn.tx_date} onChange={(e) => setInn({ ...inn, tx_date: e.target.value })} className="h-9" data-testid="in-date" /></Field>
            <Field label="Bank Name *">
              <div className="flex gap-1">
                <Select value={inn.bank_id} onValueChange={setBank(setInn, inn)}>
                  <SelectTrigger className="h-9 flex-1" data-testid="in-bank"><SelectValue placeholder="Select bank" /></SelectTrigger>
                  <SelectContent className="bg-white">{banks.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
                </Select>
                <Button type="button" onClick={() => setShowAddBank(true)} className="h-9 w-9 p-0 bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-300" title="Add new bank" data-testid="in-add-bank"><Plus className="h-4 w-4" /></Button>
              </div>
            </Field>
            <Field label="Bill No *">
              <Input value={inn.doc_no} onChange={(e) => setInn({ ...inn, doc_no: e.target.value })} onBlur={(e) => lookupDocNo(e.target.value)} placeholder="e.g. 0020" className="h-9 font-mono-num" data-testid="in-doc-no" />
            </Field>
            <Field label="Service Name (auto)"><Input value={inn.service_name} readOnly placeholder="— auto-fetched —" className="h-9 bg-slate-50" data-testid="in-service-name" /></Field>
            <Field label="Assigned Person (auto)"><Input value={inn.assigned_person} readOnly placeholder="— auto-fetched —" className="h-9 bg-slate-50" data-testid="in-assigned-person" /></Field>
            <Field label="Amount *"><Input type="number" value={inn.amount} onChange={(e) => setInn({ ...inn, amount: e.target.value })} placeholder="0.00" className="h-9 font-mono-num" data-testid="in-amount" /></Field>
            <Field label="Operator">
              <Select value={inn.operator} onValueChange={(v) => setInn({ ...inn, operator: v })}>
                <SelectTrigger className="h-9" data-testid="in-operator"><SelectValue placeholder="Select operator" /></SelectTrigger>
                <SelectContent className="bg-white">{users.map((u) => <SelectItem key={u.id} value={u.name}>{u.name}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <div className="md:col-span-3 flex justify-end pt-2">
              <Button onClick={saveIn} className="h-9 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold" data-testid="in-save"><Save className="h-4 w-4 mr-1.5" /> Save Collection</Button>
            </div>
          </div>
        )}
      </div>

      {/* List */}
      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <div className="px-4 py-2.5 border-b border-slate-200 bg-slate-50 font-display font-bold text-slate-800 text-sm">
          Recent {tab === "out" ? "Payments" : "Collections"} ({txList.length})
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm crm-table" data-testid="btx-table">
            <thead className="bg-slate-50 text-slate-600 text-[10px] uppercase tracking-wider">
              <tr>
                <th className="px-3 py-2 text-left">Date</th>
                <th className="px-3 py-2 text-left">Bank</th>
                {tab === "out" ? (<><th className="px-3 py-2 text-left">Credit To</th><th className="px-3 py-2 text-left">Debit From</th></>) : (<><th className="px-3 py-2 text-left">Bill No</th><th className="px-3 py-2 text-left">Service</th></>)}
                <th className="px-3 py-2 text-right">Amount</th>
                <th className="px-3 py-2 text-left">{tab === "out" ? "Payment" : "Assigned To"}</th>
                <th className="px-3 py-2 text-left">Operator</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {txList.length === 0 ? (
                <tr><td colSpan={8} className="py-8 text-center text-slate-400">No transactions yet</td></tr>
              ) : txList.map((t) => (
                <tr key={t.id}>
                  <td className="px-3 py-2 font-mono-num text-slate-600 text-xs">{t.tx_date}</td>
                  <td className="px-3 py-2 font-semibold text-slate-800">{t.bank_name || "—"}</td>
                  {tab === "out" ? (
                    <>
                      <td className="px-3 py-2 text-slate-700">{t.credit_name || "—"}</td>
                      <td className="px-3 py-2 text-slate-500 text-xs">{t.debit_name || "—"}</td>
                    </>
                  ) : (
                    <>
                      <td className="px-3 py-2 font-mono-num text-slate-700">{shortBill(t.doc_no)}</td>
                      <td className="px-3 py-2 text-slate-700">{t.service_name || "—"}</td>
                    </>
                  )}
                  <td className={`px-3 py-2 text-right font-mono-num font-bold ${tab === "out" ? "text-red-600" : "text-emerald-600"}`}>{inr(t.amount)}</td>
                  <td className="px-3 py-2 text-xs text-slate-500">{tab === "out" ? (t.payment_type || "—") : (t.assigned_person || "—")}</td>
                  <td className="px-3 py-2 text-xs text-slate-500">{t.operator || t.created_by || "—"}</td>
                  <td className="px-3 py-2 text-right">
                    {currentUser?.role === "admin" && (
                      <button onClick={() => removeTx(t.id)} className="p-1.5 rounded hover:bg-red-50 text-red-600" title="Delete" data-testid={`btx-del-${t.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add Bank modal */}
      {showAddBank && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setShowAddBank(false)} data-testid="add-bank-modal">
          <div className="bg-white rounded-md shadow-xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
            <div className="font-display font-bold text-slate-900 text-lg mb-4">Add Bank</div>
            <div className="space-y-3">
              <Field label="Bank Name *"><Input value={newBank.name} onChange={(e) => setNewBank({ ...newBank, name: e.target.value })} placeholder="e.g. HDFC Bank Main A/C" className="h-9" data-testid="newbank-name" /></Field>
              <Field label="Account Number"><Input value={newBank.account_number} onChange={(e) => setNewBank({ ...newBank, account_number: e.target.value })} className="h-9 font-mono-num" data-testid="newbank-ac" /></Field>
              <Field label="IFSC"><Input value={newBank.ifsc} onChange={(e) => setNewBank({ ...newBank, ifsc: e.target.value.toUpperCase() })} className="h-9 font-mono-num" data-testid="newbank-ifsc" /></Field>
              <Field label="Branch"><Input value={newBank.branch} onChange={(e) => setNewBank({ ...newBank, branch: e.target.value })} className="h-9" data-testid="newbank-branch" /></Field>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <Button onClick={() => setShowAddBank(false)} className="h-9 bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-300">Cancel</Button>
              <Button onClick={saveNewBank} className="h-9 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="newbank-save"><Save className="h-4 w-4 mr-1.5" /> Save Bank</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1">
      <Label className="text-[10px] font-semibold uppercase tracking-wider text-slate-600">{label}</Label>
      {children}
    </div>
  );
}
